import { Component, createRef } from 'react';

import { IOC_TYPES } from '../../../context/ioc.types';
import { lazyInject } from '../../../context/module';
import { FileUpload } from '../../../models/file-upload';
import { Logger } from '../../../models/logger';
import { UploadInProgressFile } from '../../../models/upload-in-progress-file';
import { FileManagerService } from '../../../services/file-manager.service';
import { FileProcessService } from '../../../services/file-process.service';
import { FileUploadService } from '../../../services/file-upload.service';
import { PageDataProviderService } from '../../../services/page-data-provider.service';
import { ScriptLoadService } from '../../../services/script-load.service';
import { SpinnerService } from '../../../services/spinner.service';
import { UIContext } from '../../../services/ui-context';
import { WebSocketService } from '../../../services/websocket.service';
import { ConvertOptionCategories } from '../../../types/dropdown.type';
import { OrientationUpdateEvent } from '../../../types/types';
import { FileUploadUtils } from '../../../utils/file-upoad.utils';
import { cloneDeep, merge } from '../../../utils/lodash-min';
import { untilDestroyed } from '../../../utils/until-destroyed';
import { ConvertOptionsContext, HTMLWidgetChangeEvent } from '../../convert-options/convert-options-context';
import ConvertOptionsComponent from '../../convert-options/convert-options.component';
import FileCardComponent, { UpdateProcessProbedDataFnProvider } from '../file-card/file-card.component';
import Modal from '../modal/modal.component';
import SortableComponent from '../sortable/sortable.component';

const RE_EDITED_FILES = 'RE_EDITED_FILES';


interface FileUploadReEditModalComponentProps {
    fileUpload: FileUpload;
}

export class FileUploadReEditModalComponent extends Component<FileUploadReEditModalComponentProps> implements UpdateProcessProbedDataFnProvider {
    private readonly logger = Logger.getLogger('FileUploadReEditModalComponent');

    modalRef = createRef<Modal>();
    uploadInProgressFiles: UploadInProgressFile<File>[] = [];
    options: ConvertOptionCategories;
    extraProps: any;

    @lazyInject(IOC_TYPES.FileProcessService) fileProcessService: FileProcessService;
    @lazyInject(IOC_TYPES.FileUploadService) fileUploadService: FileUploadService;
    @lazyInject(IOC_TYPES.WebSocketService) websocketService: WebSocketService;
    @lazyInject(IOC_TYPES.UIContext) uiContext: UIContext;
    @lazyInject(IOC_TYPES.FileManagerService) fileManagerService: FileManagerService;
    @lazyInject(IOC_TYPES.PageDataProviderService) optionsService: PageDataProviderService;
    @lazyInject(IOC_TYPES.SpinnerService) spinnerService: SpinnerService;
    @lazyInject(IOC_TYPES.ScriptLoadService) scriptLoadService: ScriptLoadService;

    constructor(props) {
        super(props);
    }

    async componentDidMount() {
        const instance = await this.spinnerService.show('Loading....');
        this.uploadInProgressFiles = await FileUploadUtils.toUploadInProgressFile(this.props.fileUpload);
        const options = this.props.fileUpload.optionsSnapshot;
        this.optionsService.loadConvertOptions(this.props.fileUpload.uniqueProcessType).subscribe({
            next: (functionalOptions) => {
                this.options = merge(functionalOptions, options);

                const ctx = ConvertOptionsContext.newContext(this.options);
                ctx.onSelectedOptionsChanged.pipe(untilDestroyed(this)).subscribe({
                    next: (evnt: HTMLWidgetChangeEvent) => {
                        this.convertOptionCategoriesChanged(evnt);
                    }
                });

                this.extraProps = {
                    convertOptionCategories: this.options,
                    convertOptionsContext: ctx,
                    fileUpload: this.props.fileUpload,
                    onOptionChange: (data) => {
                        console.log(`Data from ConvertOptionsComponent `, data);
                    },
                };
                console.log(this.uploadInProgressFiles);
                this.spinnerService.hide(instance);
                this.forceUpdate();
            },
            error: (err) => {
                this.spinnerService.hide(instance);
                this.uiContext.handleError(err);
            }
        })

        // TODO if this failed uniqueProcessType options will be broken for all future converts until a page refresh
        // this.optionsService.loadConvertOptions(this.props).subscribe({
        //     next: (functionalOptions) => {
        //         this.options = merge(functionalOptions, options);
        //     }
        // });
        // TODO if this failed uniqueProcessType options will be broken for all future converts until a page refresh

        // this.optionsService.getOptionsFor(this.fileUpload.uniqueProcessType);

    }

    convertOptionCategoriesChanged(event: HTMLWidgetChangeEvent) {
        if (event && event.selectedValue && event.formChild && event.formChild.ctxCallbackFn) {
            event.formChild.ctxCallbackFn(this.props.fileUpload, event.selectedValue.formName);
            this.forceUpdate();
        }
    }

    orientationChanged(orientationData: OrientationUpdateEvent) {
        const uploadInProgressFile = this.uploadInProgressFiles.find(fm => fm.getFile() === orientationData.file);
        if (!uploadInProgressFile) {
            this.logger.trace1('Set orientation was called on a file that is not on uploadInProgressFiles list');
            return;
        }
        uploadInProgressFile.setOrientation(orientationData.orientation);
        this.forceUpdate();
    }

    removeFile(file) {
        let index = -1;
        this.uploadInProgressFiles.forEach((rowFileAndData, i) => {
            if (rowFileAndData.getFile() === file) {
                index = i;
            }
        });
        if (index !== -1) {
            this.uploadInProgressFiles.splice(index, 1);
        }
        this.forceUpdate();
    }

    public updateProcessProbedDataFn(): (probeDuration: number) => Promise<any> {
        // TODO this get called millions of times. Need a better way to parse the dataProbeFn to file-card
        const that = this;
        return async (probeDuration: number) => {
            if (that.props.fileUpload.processType !== 'merge') {
                const fileUpload = that.convertMergeUploadInProgressFilesToFileUploads();
                return await that.fileProcessService.probeProcessForInfo(fileUpload, probeDuration);
            }
            console.log('Nothing to do');
        };
    }

    public convert() {
        // TODO Preact removed
        // this.ngZone.runOutsideAngular(() => {
        if (this.props.fileUpload.processType === 'merge') {
            const fileUpload = this.convertMergeUploadInProgressFilesToFileUploads();
            // TODO Preact removed
            // this.ngZone.run(() => {
            this.fileManagerService.addFileUpload(fileUpload);
            // });
            this.fileProcessService.processFile(fileUpload).subscribe({
                next: (data) => this.logger.trace7(data),
                error: (err) => {
                    this.logger.error({
                        message: 'File process failed on file upload reedit modal.',
                        error: err,
                        data: FileUpload.toLoggableObject(fileUpload),
                    });
                    this.uiContext.showErrorDialog({ title: 'Error', text: `Failed to ${this.props.fileUpload.processType} the file.` });
                }
            });
        } else {
            const fileUploads = this.convertNoneMergeUploadInProgressFilesToFileUploads();
            fileUploads.forEach(fileUpload => {
                this.fileManagerService.addFileUpload(fileUpload);
                this.fileProcessService.processFile(fileUpload).subscribe({
                    next: (data) => this.logger.trace7(data),
                    error: (err) => {
                        this.logger.error({
                            message: 'File process failed on file upload reedit modal.',
                            error: err,
                            data: FileUpload.toLoggableObject(fileUpload),
                        });
                    }
                });
            });
        }
        this.hide();
        // });
    }

    convertMergeUploadInProgressFilesToFileUploads(): FileUpload {
        const optionsSnapshot: ConvertOptionCategories = cloneDeep(this.props.fileUpload.optionsSnapshot);
        console.log(optionsSnapshot);

        const options = [];
        options.push(...this.extraProps.convertOptionsContext.toConvertOptionsSummary());
        const orientationAngleList: string[] =
            this.uploadInProgressFiles.map(fm => (fm.getOrientation() && fm.getOrientation().angle) || '0');
        const orientationFlipList: string[] =
            this.uploadInProgressFiles.map(fm => (fm.getOrientation() && fm.getOrientation().isFliped) || 'false');
        options.push(
            { formName: 'orientationAngleList', formValue: orientationAngleList },
            { formName: 'orientationFlipList', formValue: orientationFlipList },
        );
        const fileUploads: FileUpload[] = this.uploadInProgressFiles.map(fmd => {
            return FileUploadUtils.toFileUpload(fmd, this.websocketService.getId(),
                this.props.fileUpload.processType,
                this.props.fileUpload.fromType,
                this.props.fileUpload.toType,
                options,
                optionsSnapshot,
                fmd.uniqueProcessType,
                this.props.fileUpload.pageData,
            );
        });

        const fileUpload = fileUploads.splice(0, 1)[0];
        fileUpload.metadataList = [cloneDeep(fileUpload.metadata)]; // now fileUploads doesnt have this
        fileUploads.forEach(fu => {
            fileUpload.originalInputFiles.push(...fu.originalInputFiles);
            fileUpload.metadataList.push(cloneDeep(fu.metadata));
        });

        fileUpload.result = undefined;
        fileUpload.zipResult = undefined;
        return fileUpload;
    }

    convertNoneMergeUploadInProgressFilesToFileUploads(): FileUpload[] {
        return this.uploadInProgressFiles.map(fmd => {
            return this.convertNoneMergeUploadInProgressFilesToFileUpload(fmd);
        });
    }

    convertNoneMergeUploadInProgressFilesToFileUpload(fmd: UploadInProgressFile<File>): FileUpload {
        // following is not the right snapshot we need to save on this fileUpload
        const optionsSnapshot: ConvertOptionCategories = cloneDeep(this.extraProps.convertOptionsContext.convertOptionCategories);
        console.log(optionsSnapshot);
        const options = [];
        options.push(...this.extraProps.convertOptionsContext.toConvertOptionsSummary());
        const orientationAngleList: string[] = [(fmd.getOrientation() && fmd.getOrientation().angle) || '0'];
        const orientationFlipList: string[] = [(fmd.getOrientation() && fmd.getOrientation().isFliped) || 'false'];
        options.push(
            { formName: 'orientationAngleList', formValue: orientationAngleList },
            { formName: 'orientationFlipList', formValue: orientationFlipList },
        );

        const fileUpload = FileUploadUtils.toFileUpload(
            fmd,
            this.websocketService.getId(),
            this.props.fileUpload.processType,
            this.props.fileUpload.fromType,
            this.props.fileUpload.toType,
            options,
            optionsSnapshot,
            fmd.uniqueProcessType,
            this.props.fileUpload.pageData,
        );

        fileUpload.metadataList = [cloneDeep(fileUpload.metadata)];
        fileUpload.originalInputFiles = cloneDeep(fileUpload.originalInputFiles);
        fileUpload.result = undefined;
        fileUpload.zipResult = undefined;
        return fileUpload;
    }

    hide() {
        this.modalRef && this.modalRef.current && this.modalRef.current.unmount();
    }

    sortOrderChanged(e) {
        // Skipped
    }

    sortEnded(e) {
        this.uploadInProgressFiles.splice(0, this.uploadInProgressFiles.length, ...e);
        this.forceUpdate();
    }


    render() {
        return <Modal ref={this.modalRef}>
            <div data-modalheader>
                <h4 className="modal-title">Edit and restart process</h4>
            </div>
            <div className="modal-body contact-us-modal" data-modalbody>
                {
                    // Preact fix [sortablejs]="uploadInProgressFiles"
                    this.uploadInProgressFiles && <div className="mt-3 mb-3">
                        {
                            <SortableComponent
                                sortHandlerClass='sort-handler'
                                items={this.uploadInProgressFiles}
                                orderChanged={(e) => this.sortOrderChanged(e)}
                                sortEnded={(e) => this.sortEnded(e)}>
                                {
                                    this.uploadInProgressFiles.map((uploadInProgressFile, idx) => {
                                        return <div key={'sortable-content-' + idx} className={`sortable ${uploadInProgressFile.file.type.indexOf('image') === -1 ? 'non-image mb-3 mr-3' : 'image mb-3 mr-3'}`}>
                                            <FileCardComponent
                                                convertOptionsContext={this.extraProps.convertOptionsContext}
                                                uploadInProgressFile={uploadInProgressFile}
                                                convertOptionCategories={this.options}
                                                updateProcessProbedDataFnProvider={this}
                                                toType={this.props.fileUpload.toType}
                                                fromType={this.props.fileUpload.fromType}
                                                removeFile={$event => this.removeFile($event)}
                                                orientationChanged={$event => this.orientationChanged($event)}>
                                            </FileCardComponent>
                                        </div>
                                    })
                                }
                            </SortableComponent>
                        }
                    </div>
                }
                {
                    this.options && <ConvertOptionsComponent
                        url={this.props.fileUpload.uniqueProcessType}
                        {...this.extraProps}
                    ></ConvertOptionsComponent>
                }
            </div>
            <div data-modalfooter>
                <button className="x-btn x-btn-primary mr-1" onClick={() => this.convert()}>Restart Process With New Options</button>
                <button className="x-btn x-btn-gray" onClick={() => this.hide()}>Close</button>
            </div>
        </Modal>
    }
}
